'use client';

import React, { useEffect, useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { startPurchase, poundsLabel } from '@/lib/limits';
import { fetchExtraPrices, DEFAULT_EXTRA_PRICES } from '@/lib/siteSettings';

/**
 * A member posting their own haulage job, paid for.
 *
 * The board is still admin-managed — Alex adds jobs himself for nothing — and
 * this sits alongside that: a member fills the form, pays, and the job goes
 * straight onto the board.
 *
 * Nothing is written until the payment lands. The job details travel to Stripe
 * as part of the pending purchase and the webhook creates the board entry, so an
 * abandoned checkout leaves no half-posted job behind.
 */

const LIVESTOCK = ['Cattle', 'Sheep', 'Pigs', 'Horses', 'Machinery', 'General'];

export default function PostHaulageJobPanel() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [price, setPrice] = useState(DEFAULT_EXTRA_PRICES.haulageJobPence);
  const [form, setForm] = useState({
    title: '',
    category: 'Cattle',
    quantity: '',
    from: '',
    fromCounty: '',
    to: '',
    toCounty: '',
    dateRequired: '',
    budget: '',
    description: '',
  });

  useEffect(() => {
    let live = true;
    fetchExtraPrices(createClient())
      .then((p) => { if (live) setPrice(p.haulageJobPence); })
      .catch(() => {});
    return () => { live = false; };
  }, []);

  const label = poundsLabel(price);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const url = await startPurchase({
        kind: 'haulage_job',
        job: {
          title: form.title,
          subtitle: [form.quantity, form.dateRequired && `needed ${form.dateRequired}`]
            .filter(Boolean)
            .join(' · '),
          description: form.description,
          category: form.category,
          location_town: form.from,
          county: form.fromCounty,
          coverage: [form.from, form.to].filter(Boolean).join(' to '),
          price: form.budget.replace(/[^0-9.]/g, ''),
          price_note: form.budget ? 'budget' : '',
          details: Object.fromEntries(
            [
              ['Livestock', form.category],
              ['Quantity', form.quantity],
              ['From', [form.from, form.fromCounty].filter(Boolean).join(', ')],
              ['To', [form.to, form.toCounty].filter(Boolean).join(', ')],
              ['Date required', form.dateRequired],
            ].filter(([, v]) => Boolean(v))
          ),
        },
      });
      window.location.href = url;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not start that payment.');
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <div className="card mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="font-bold text-foreground text-sm">Got a movement that needs covering?</h2>
          <p className="text-sm text-muted-foreground mt-1">
            List your own haulage job and hauliers will get in touch. {label} per job.
          </p>
        </div>
        <button className="btn-primary text-sm shrink-0" onClick={() => setOpen(true)}>
          <Icon name="PlusCircleIcon" size={16} />
          List a haulage job — {label}
        </button>
      </div>
    );
  }

  return (
    <div className="card mb-6">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h2 className="font-bold text-foreground">List a haulage job</h2>
          <p className="text-sm text-muted-foreground mt-1">
            {label} per job. It goes live as soon as the payment clears.
          </p>
        </div>
        <button
          type="button"
          onClick={() => { setOpen(false); setError(null); }}
          className="text-muted-foreground hover:text-foreground p-1"
          aria-label="Close"
        >
          <Icon name="XMarkIcon" size={20} />
        </button>
      </div>

      {!user && (
        <div className="flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-xl px-5 py-4 mb-4">
          <Icon name="InformationCircleIcon" size={20} className="text-blue-600 shrink-0 mt-0.5" />
          <p className="text-sm text-blue-900">
            <span className="font-bold">You&apos;re not signed in.</span> You can fill this in, but
            you&apos;ll need an account to pay so we know whose job it is.{' '}
            <a href="/sign-up-login-screen" className="underline font-semibold">Sign in or register</a>.
          </p>
        </div>
      )}

      {error && (
        <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl px-5 py-4 mb-4" role="alert">
          <Icon name="ExclamationTriangleIcon" size={20} className="text-red-600 shrink-0 mt-0.5" />
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2">
          <label className="form-label text-xs" htmlFor="hj-title">What needs moving? *</label>
          <input
            id="hj-title"
            className="form-input"
            required
            value={form.title}
            onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            placeholder="e.g. 40 store cattle, Devon to Cumbria"
          />
        </div>

        <div>
          <label className="form-label text-xs" htmlFor="hj-category">Type</label>
          <select
            id="hj-category"
            className="form-input"
            value={form.category}
            onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
          >
            {LIVESTOCK.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>

        <div>
          <label className="form-label text-xs" htmlFor="hj-qty">How many / how much</label>
          <input
            id="hj-qty"
            className="form-input"
            value={form.quantity}
            onChange={(e) => setForm((f) => ({ ...f, quantity: e.target.value }))}
            placeholder="e.g. 40 head"
          />
        </div>

        <div>
          <label className="form-label text-xs" htmlFor="hj-from">Collect from (town)</label>
          <input
            id="hj-from"
            className="form-input"
            value={form.from}
            onChange={(e) => setForm((f) => ({ ...f, from: e.target.value }))}
          />
        </div>
        <div>
          <label className="form-label text-xs" htmlFor="hj-fromc">Collect from (county)</label>
          <input
            id="hj-fromc"
            className="form-input"
            value={form.fromCounty}
            onChange={(e) => setForm((f) => ({ ...f, fromCounty: e.target.value }))}
          />
        </div>

        <div>
          <label className="form-label text-xs" htmlFor="hj-to">Deliver to (town)</label>
          <input
            id="hj-to"
            className="form-input"
            value={form.to}
            onChange={(e) => setForm((f) => ({ ...f, to: e.target.value }))}
          />
        </div>
        <div>
          <label className="form-label text-xs" htmlFor="hj-toc">Deliver to (county)</label>
          <input
            id="hj-toc"
            className="form-input"
            value={form.toCounty}
            onChange={(e) => setForm((f) => ({ ...f, toCounty: e.target.value }))}
          />
        </div>

        <div>
          <label className="form-label text-xs" htmlFor="hj-date">When</label>
          <input
            id="hj-date"
            className="form-input"
            value={form.dateRequired}
            onChange={(e) => setForm((f) => ({ ...f, dateRequired: e.target.value }))}
            placeholder="e.g. w/c 25 August, or flexible"
          />
        </div>
        <div>
          <label className="form-label text-xs" htmlFor="hj-budget">Budget (£)</label>
          <input
            id="hj-budget"
            type="text"
            inputMode="decimal"
            className="form-input"
            value={form.budget}
            onChange={(e) => setForm((f) => ({ ...f, budget: e.target.value }))}
            placeholder="Leave blank for POA"
          />
        </div>

        <div className="sm:col-span-2">
          <label className="form-label text-xs" htmlFor="hj-desc">Anything else a haulier should know</label>
          <textarea
            id="hj-desc"
            rows={3}
            className="form-input resize-none"
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            placeholder="Access, loading facilities, whether you can be flexible on dates…"
          />
        </div>

        <div className="sm:col-span-2 flex flex-wrap items-center gap-3">
          <button
            type="submit"
            className="btn-primary text-sm disabled:opacity-60"
            disabled={busy || !user}
          >
            <Icon
              name={busy ? 'ArrowPathIcon' : 'CreditCardIcon'}
              size={16}
              className={busy ? 'animate-spin' : undefined}
            />
            {busy ? 'Taking you to payment…' : `Pay ${label} and list it`}
          </button>
          <button
            type="button"
            className="btn-secondary text-sm"
            onClick={() => { setOpen(false); setError(null); }}
          >
            Cancel
          </button>
          <p className="text-xs text-muted-foreground">
            You pay by card on Stripe. Nothing is posted until the payment goes through.
          </p>
        </div>
      </form>
    </div>
  );
}
